import { ReactElement } from "react";
import {
	DateString,
	Icon,
	IconAndText,
	ListItem,
	Space,
	PipeSeparator
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { HubThumbnail } from "client/Features/Hub/HubThumbnail/HubThumbnail";
import styles from "./HubListItem.module.less";
import { HubItemMediaPreviewViewModel } from "client/Api/Hub/common";
import { HubItemCategoryModel } from "client/Api/Hub/hub";
import { PreviewItemUserModel } from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import { NumberFormatter } from "client/Hooks";
import { useHubStatisticsFormatter } from "client/Features/Hub/useHubStatisticsFormatter";
import { TimeZone } from "client/DateAndTime/timezones";

export type HubListItemProps = {
	title: string;
	media: HubItemMediaPreviewViewModel;
	lastModifiedDate: Date | string;
	category: HubItemCategoryModel | undefined;
	numberOfViews: number;
	author: PreviewItemUserModel | null;
	onNavigate(): void;
	trackingId: string;
	isPinned: boolean;
	timezone: TimeZone;
	isNew: boolean;
};

export const HubListItem = (props: HubListItemProps): ReactElement => {
	const {
		title,
		media,
		lastModifiedDate,
		category,
		numberOfViews,
		author,
		onNavigate,
		trackingId,
		isPinned,
		timezone,
		isNew
	} = props;

	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();

	return (
		<ListItem
			trackingId={trackingId}
			hasStatusDot={isNew}
		>
			<ListItem.Content
				onClick={onNavigate}
				thumbnail={(
					<div className={styles.thumbnail}>
						<HubThumbnail
							title={title}
							media={media}
							hubItemKind={HubItemKind.KnowledgeItem}
							mediaPlaceholderSize="small"
						/>
					</div>
				)}
			>
				<ListItem.Row>
					{isPinned && (
						<ListItem.TitleIcon kind="thumbtack" color="primary" />
					)}
					<ListItem.Title>
						{title}
					</ListItem.Title>
				</ListItem.Row>
				<ListItem.SubTitle>
					<Icon value="pen" />
					<Space />
					<DateString
						value={lastModifiedDate}
						format="RelativeTimestamp"
						timezone={timezone}
					/>
					{author?.name && (
						<>
							<PipeSeparator />
							{localize("_(Edited)By")} <Space /> {author.name}
						</>
					)}
					{category && (
						<>
							<PipeSeparator />
							{category.name}
						</>
					)}
				</ListItem.SubTitle>
				<div className={styles.stats}>
					<IconAndText
						text={statisticsFormatter(numberOfViews)}
					>
						<IconAndText.Icon
							value="eye"
						/>
					</IconAndText>
				</div>
			</ListItem.Content>
		</ListItem>
	);
};

HubListItem.displayName = "HubListItem";
